import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, Microscope, Search } from "lucide-react";
import CTAButton from "../components/CTAButton";
import SectionHeading from "../components/SectionHeading";
import Reveal from "../components/Reveal";

const WHAT_YOU_FIND = [
  { icon: Microscope, title: "Skin science explained", desc: "How skin actually ages, heals, and responds to common ingredients." },
  { icon: Sparkles, title: "Healthy-aging basics", desc: "What the research says about sleep, sun, stress, and skin over time." },
  { icon: Search, title: "Myth-busting", desc: "Common skincare and anti-aging claims held up against the evidence." },
  { icon: BookOpen, title: "Ingredient guides, with sources", desc: "Retinoids, sunscreen, vitamin C, peptides &mdash; what the studies actually show." },
];

const PRINCIPLES = [
  { t: "Evidence-informed", d: "Plain-language translation of current clinical guidance and published research." },
  { t: "Clinician-written", d: "Written and reviewed by a registered nurse, not a marketing team." },
  { t: "No hype", d: "No miracle claims, no fear-based marketing. Sources cited." },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative pt-36 md:pt-44 pb-20 md:pb-32 px-6 md:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <Reveal className="md:col-span-7">
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-sage font-medium mb-6">
              <span className="gold-divider mr-3" /> Skincare &amp; Healthy Aging
            </p>
            <h1 className="font-heading text-[44px] leading-[1.05] md:text-7xl md:leading-[1.02] text-navy tracking-tight text-balance">
              Evidence-based<br />skincare and<br />
              <span className="italic text-navy/90">healthy aging.</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-charcoal/80 max-w-xl leading-relaxed">
              Real research, translated simply &mdash; by a registered nurse. No hype, no miracle claims.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <CTAButton to="/learn" variant="primary" data-testid="hero-cta-read">Read the articles</CTAButton>
            </div>
            <div className="mt-12 flex items-center gap-6 text-xs uppercase tracking-[0.22em] text-charcoal/60">
              <span>Evidence-Informed</span>
              <span className="gold-divider" />
              <span>Nurse-Written</span>
              <span className="gold-divider" />
              <span>Sources Cited</span>
            </div>
          </Reveal>

          <Reveal className="md:col-span-5" delay={150}>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-sage/30 rounded-sm hidden md:block" />
              <div className="absolute -bottom-8 -right-6 w-44 h-44 border border-gold/60 rounded-sm hidden md:block" />
              <img
                data-testid="hero-image"
                src="https://images.pexels.com/photos/7615460/pexels-photo-7615460.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=940"
                alt="Reading skincare research"
                className="relative w-full h-[420px] md:h-[560px] object-cover rounded-sm shadow-xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY THIS SITE EXISTS */}
      <section className="bg-sage/10 py-24 md:py-32 px-6 md:px-12 border-y border-navy/5">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.28em] text-sage font-medium mb-5">
              <span className="gold-divider mr-3" /> Why this site exists
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-navy leading-tight tracking-tight text-balance">
              Most skincare advice online is written to sell you something.
            </h2>
            <p className="mt-7 text-lg md:text-xl text-charcoal/80 leading-relaxed max-w-2xl mx-auto">
              This site exists for the opposite reason &mdash; to explain what
              the evidence actually shows, in plain language, so you can make
              your own informed decisions.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT YOU'LL FIND HERE */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <SectionHeading
              overline="What you'll find here"
              title="Plain-language articles across four themes."
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {WHAT_YOU_FIND.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 80}>
                  <div data-testid={`find-${i}`} className="bg-white border border-navy/10 p-8 rounded-sm hover-lift h-full flex gap-5 items-start">
                    <span className="mt-1 w-11 h-11 shrink-0 flex items-center justify-center bg-navy/5 border border-navy/10 rounded-sm text-gold">
                      <Icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-heading text-2xl text-navy">{item.title}</h3>
                      <p className="mt-2 text-sm text-charcoal/75 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* THREE PRINCIPLES */}
      <section className="bg-offwhite border-t border-navy/5 py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <SectionHeading
              overline="Three principles"
              title="How every article on this site is written."
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.t} delay={i * 100}>
                <div data-testid={`principle-${i}`} className="bg-white border border-navy/10 p-8 rounded-sm hover-lift h-full">
                  <p className="text-[11px] tracking-[0.25em] uppercase text-gold">Principle 0{i + 1}</p>
                  <h3 className="mt-2 font-heading text-2xl text-navy">{p.t}</h3>
                  <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-navy py-24 md:py-32 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-gold/10 rounded-full blur-3xl" aria-hidden />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-sage/15 rounded-full blur-3xl" aria-hidden />
        <div className="max-w-5xl mx-auto text-center relative">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold mb-6">
              <span className="inline-block w-12 h-px bg-gold mr-3 align-middle" />
              Start Reading
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.1] tracking-tight">
              Learn something today.
            </h2>
            <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl mx-auto">
              Short, plain-language articles on skin science, healthy aging, and skincare myths &mdash; each one backed by published research.
            </p>
            <div className="mt-10">
              <Link
                to="/learn"
                data-testid="cta-band-learn"
                className="inline-flex items-center justify-center px-8 py-3.5 bg-gold text-navy text-xs tracking-[0.22em] uppercase font-medium rounded-sm hover:bg-gold/90 hover-lift"
              >
                Read the articles
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
